
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, UserPlus } from "lucide-react";
import { AdminUsersList } from "./AdminUsersList";
import { CreateUserDialog } from "./CreateUserDialog";
import { AdminUser } from "@/types/admin";
import { supabase } from "@/integrations/supabase/client";

export const UserManagement = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      // Use the new secure function to get user roles with emails
      const { data: userRolesData, error: rolesError } = await supabase
        .rpc('get_user_roles_with_email');
        
      if (rolesError) {
        throw rolesError;
      }
      
      // Also get all users for those without roles
      const { data: allUsersData, error: usersError } = await supabase
        .rpc('get_users_for_admin');
        
      if (usersError) {
        throw usersError;
      }
      
      // Create a map of users
      const usersMap = new Map();
      
      // First add all users from the admin users view
      if (allUsersData) {
        allUsersData.forEach(user => {
          usersMap.set(user.id, {
            id: user.id,
            email: user.email,
            full_name: user.full_name,
            avatar_url: user.avatar_url,
            created_at: user.created_at,
            roles: user.roles || [],
            login_count: user.login_count || 0
          });
        });
      }
      
      // Then enhance them with roles from the user_roles_with_email function
      if (userRolesData) {
        userRolesData.forEach(roleData => {
          const userId = roleData.user_id;
          if (usersMap.has(userId)) {
            // If user exists, add role to roles array if it's not already there
            const user = usersMap.get(userId);
            if (!user.roles.includes(roleData.role)) {
              user.roles.push(roleData.role);
            }
          }
        });
      }
      
      // Convert map to array
      const combinedUsers = Array.from(usersMap.values());
      
      console.log("Combined users data:", combinedUsers);
      setUsers(combinedUsers);
    } catch (error: any) {
      console.error("Error fetching users:", error);
      toast.error(`Failed to load users: ${error.message}`);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleUserCreated = () => {
    fetchUsers();
    setShowCreateDialog(false);
    toast.success("User created successfully");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">Users ({users.length})</h2>
        <Button onClick={() => setShowCreateDialog(true)}>
          <UserPlus className="mr-2 h-4 w-4" /> Create User
        </Button>
      </div>
      
      {loading ? (
        <div className="flex justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <AdminUsersList users={users} onUserUpdated={fetchUsers} />
      )}
      
      <CreateUserDialog 
        open={showCreateDialog} 
        onOpenChange={setShowCreateDialog} 
        onUserCreated={handleUserCreated}
      />
    </div>
  );
};
